
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { auth, db } from '../firebase';
import { signOut } from 'firebase/auth';
import { addDoc, collection, serverTimestamp, query, where, onSnapshot, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from './ui';

interface LayoutProps {
  children: React.ReactNode;
  fluid?: boolean;
}

const Layout: React.FC<LayoutProps> = ({ children, fluid = false }) => {
  const { userProfile, schoolProfile, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  
  const [activeBroadcast, setActiveBroadcast] = useState(false);
  const [scheduledBroadcast, setScheduledBroadcast] = useState<{name: string, time: string} | null>(null);
  const [unreadDMs, setUnreadDMs] = useState(0);
  const [connectionCount, setConnectionCount] = useState(0);
  const [unreadMentions, setUnreadMentions] = useState(0);

  // Global Presence Heartbeat
  useEffect(() => {
    if (!currentUser || !userProfile) return;

    const updatePresence = async () => {
      // If user has chosen to hide their status, we delete the presence doc immediately if it exists
      if (userProfile.isOnlineHidden) {
        await deleteDoc(doc(db, 'presence', currentUser.uid)).catch(() => {});
        return;
      }

      const presenceDocRef = doc(db, 'presence', currentUser.uid);
      await setDoc(presenceDocRef, {
        uid: currentUser.uid,
        name: userProfile.nickname || userProfile.displayName,
        role: userProfile.role,
        isTeamMember: userProfile.schoolId === 'dadsmatter',
        lastActive: serverTimestamp(),
        // currentRoomId is handled individually in Forum component but global presence is here
      }, { merge: true }).catch(() => {});
    };

    updatePresence();
    const interval = setInterval(updatePresence, 45000); // Heartbeat every 45s

    const handleUnload = () => {
        deleteDoc(doc(db, 'presence', currentUser.uid)).catch(() => {});
    };
    window.addEventListener('beforeunload', handleUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener('beforeunload', handleUnload);
      deleteDoc(doc(db, 'presence', currentUser.uid)).catch(() => {});
    };
  }, [currentUser?.uid, userProfile?.isOnlineHidden]);

  // Monitor Live & Scheduled Status & Connections
  useEffect(() => {
    // 1. Check for active live sessions
    const liveQuery = query(collection(db, 'broadcast_sessions'), where('status', '==', 'live'));
    const unsubLive = onSnapshot(liveQuery, (snap) => {
        setActiveBroadcast(!snap.empty);
    });

    // 2. Check for scheduled upcoming broadcast
    const unsubSched = onSnapshot(doc(db, 'config', 'broadcast_schedule'), (snap) => {
        if (snap.exists()) {
            const data = snap.data();
            setScheduledBroadcast({
                name: data.nextSpeaker || '',
                time: data.scheduledAt || ''
            });
        }
    });

    // 3. Monitor Unread DMs (pending requests) and Total Connections
    if (currentUser) {
      // Pending requests count for the red badge
      const qPending = query(
        collection(db, 'connections'),
        where('users', 'array-contains', currentUser.uid),
        where('status', '==', 'pending'),
        where('receiverUid', '==', currentUser.uid)
      );
      const unsubPending = onSnapshot(qPending, (snap) => {
        setUnreadDMs(snap.size); 
      });

      // Total accepted connections for the label display
      const qAccepted = query(
        collection(db, 'connections'),
        where('users', 'array-contains', currentUser.uid),
        where('status', '==', 'accepted')
      );
      const unsubAccepted = onSnapshot(qAccepted, (snap) => {
        setConnectionCount(snap.size);
      });

      // Mentions count for the pill
      let unsubMentions = () => {};
      const myName = userProfile?.nickname || userProfile?.displayName;
      if (myName) {
        const qMentions = query(
          collection(db, 'notifications'),
          where('recipientName', '==', myName),
          where('type', '==', 'mention'),
          where('read', '==', false)
        );
        unsubMentions = onSnapshot(qMentions, (snap) => {
          setUnreadMentions(snap.size);
        });
      }

      return () => { 
        unsubLive(); 
        unsubSched(); 
        unsubPending(); 
        unsubAccepted(); 
        unsubMentions();
      };
    }

    return () => {
        unsubLive();
        unsubSched();
    };
  }, [currentUser]);

  const handleLogout = async () => {
    await signOut(auth);
    navigate('/');
  };

  const userInitials = userProfile?.displayName?.charAt(0) || 'U';
  const isAdmin = userProfile?.role === 'admin';
  const isOrgManager = userProfile?.role === 'manager';
  const isDadsMatterTeam = userProfile?.schoolId === 'dadsmatter';
  
  const isProfessional = isAdmin || isOrgManager || userProfile?.role === 'staff' || isDadsMatterTeam;
  
  const isPaid = userProfile?.isPaid === true || 
                 userProfile?.subscriptionStatus === 'active' || 
                 schoolProfile?.isPaid === true ||
                 isAdmin || isDadsMatterTeam;

  const NavItem = ({ to, icon, label, isBeta, requiresPaid, isBroadcastTab, badgeCount, totalCount, mentionsCount }: { to: string, icon: string, label: string, isBeta?: boolean, requiresPaid?: boolean, isBroadcastTab?: boolean, badgeCount?: number, totalCount?: number, mentionsCount?: number }) => {
      const isActive = location.pathname === to || location.pathname.startsWith(`${to}/`);
      const isLocked = requiresPaid && !isPaid;
      
      const showLiveIndicator = isBroadcastTab && activeBroadcast;
      const showScheduled = isBroadcastTab && !activeBroadcast && scheduledBroadcast?.name;

      return (
        <Link 
            id={`nav-${to.replace('/', '')}`}
            to={to} 
            aria-label={`Navigate to ${label}`}
            className={`flex items-center gap-3 px-4 py-3 rounded-full transition-all group relative ${isActive ? 'bg-md-secondary-container text-md-on-secondary-container font-bold' : 'text-md-on-surface-variant hover:bg-md-surface-variant/50 font-medium'} ${isLocked ? 'opacity-70' : ''}`}
        >
            <div className="flex-shrink-0 flex items-center justify-center w-8 relative">
                {showLiveIndicator ? (
                    <div className="relative">
                        <div className="w-10 h-6 bg-md-error rounded-full text-[8px] font-black flex items-center justify-center text-md-on-error animate-pulse border border-white/20">ON AIR</div>
                    </div>
                ) : (
                    <span className={`text-xl ${isActive ? 'scale-110' : 'group-hover:scale-110'}`}>
                        {isLocked ? '🔒' : icon}
                    </span>
                )}
            </div>
            <div className="flex-1 flex flex-col justify-center min-w-0">
              <div className="flex items-center justify-between">
                <span className="text-sm truncate tracking-wide">
                    {label} {totalCount !== undefined && totalCount > 0 ? `(${totalCount})` : ''}
                </span>
                {isBeta && <span className={`text-[8px] font-black px-1.5 py-0.5 rounded-md ${isActive ? 'bg-white/40' : 'bg-amber-100/50 text-amber-800'} uppercase`}>Beta</span>}
                {badgeCount ? badgeCount > 0 && <span className="absolute right-4 top-1/2 -translate-y-1/2 bg-md-error text-md-on-error text-[10px] font-bold px-2 py-0.5 rounded-full z-10 shadow-sm border border-white/20">{badgeCount}</span> : null}
              </div>
              {mentionsCount ? mentionsCount > 0 && (
                  <div className="mt-0.5">
                      <span className="inline-flex items-center gap-1 bg-md-tertiary text-md-on-tertiary px-2 py-0.5 rounded-full text-[9px] font-bold shadow-sm">
                          <span>@</span> {mentionsCount} new mention{mentionsCount !== 1 ? 's' : ''}
                      </span>
                  </div>
              ) : null}
              {showScheduled && (
                  <div className="mt-0.5 flex flex-col">
                      <p className={`text-[9px] font-bold truncate ${isActive ? 'text-md-on-secondary-container/80' : 'text-md-on-surface-variant'}`}>Next: {scheduledBroadcast.name}</p>
                      <p className={`text-[8px] font-bold uppercase tracking-widest truncate ${isActive ? 'text-md-on-secondary-container/60' : 'text-slate-500'}`}>{scheduledBroadcast.time}</p>
                  </div>
              )}
            </div>
        </Link>
      );
  };

  const MobileTab = ({ to, icon, label, requiresPaid, isBroadcastTab, badgeCount, mentionsCount }: { to: string, icon: string, label: string, requiresPaid?: boolean, isBroadcastTab?: boolean, badgeCount?: number, mentionsCount?: number }) => {
    const isActive = location.pathname === to || location.pathname.startsWith(`${to}/`);
    const isLocked = requiresPaid && !isPaid;
    return (
      <Link 
        id={`mobile-nav-${to.replace('/', '')}`}
        to={to} 
        aria-label={`Navigate to ${label}`}
        className={`flex flex-col items-center justify-center flex-1 py-1 gap-1 transition-colors relative ${isActive ? 'text-md-on-surface' : 'text-md-on-surface-variant'}`}
      >
        <div className={`relative px-4 py-1 rounded-full ${isActive ? 'bg-md-secondary-container' : 'bg-transparent'} transition-colors`}>
            <span className="text-xl block">{isLocked ? '🔒' : icon}</span>
            {isBroadcastTab && activeBroadcast && (
                <span className="absolute top-0 right-1 w-2.5 h-2.5 bg-md-error rounded-full border border-md-surface animate-pulse"></span>
            )}
            {badgeCount ? badgeCount > 0 && <span className="absolute top-0 -right-2 bg-md-error text-md-on-error text-[8px] font-bold px-1.5 py-0.5 rounded-full ring-2 ring-md-surface z-10">{badgeCount}</span> : null}
            {mentionsCount ? mentionsCount > 0 && <span className="absolute -bottom-1 -right-2 bg-md-tertiary text-md-on-tertiary text-[8px] font-bold px-1.5 py-0.5 rounded-full ring-2 ring-md-surface shadow-sm z-10 flex items-center gap-0.5"><span>@</span>{mentionsCount}</span> : null}
        </div>
        <span className={`text-[10px] tracking-wide ${isActive ? 'font-bold' : 'font-medium'}`}>{label}</span>
      </Link>
    );
  };

  return (
    <div className="h-screen flex flex-col md:flex-row bg-md-surface-container-lowest overflow-hidden">
      
      {/* Desktop Sidebar: M3 Navigation Drawer */}
      <aside className="hidden md:flex flex-col w-72 bg-md-surface-container text-md-on-surface flex-shrink-0 border-r border-md-outline-variant/30">
        <div className="p-8 pb-5 flex flex-col items-center w-full relative group cursor-pointer transition-transform hover:scale-[1.02]">
            <h1 className="text-4xl font-black tracking-tighter text-md-on-surface leading-none">
                DADS<span className="text-md-primary">MATTER</span>
            </h1>
            <div className="absolute bottom-2 right-12 md:right-16">
                <p 
                    className="text-amber-500 font-bold text-xl leading-none tracking-wider drop-shadow-sm" 
                    style={{ fontFamily: "'Caveat', 'Satisfy', 'Comic Sans MS', cursive", transform: "rotate(-4deg)" }}
                >
                    Dad Hub
                </p>
            </div>
        </div>
        
        <nav className="px-3 space-y-1 flex-1 mt-0 overflow-y-auto custom-scrollbar">
          <NavItem to="/home" icon="🏠" label="Home" />
          <NavItem to="/dashboard" icon="🎓" label="My Training" requiresPaid={!isProfessional} />
          <NavItem to="/sandbox" icon="⚖️" label="LiP Sandbox" isBeta requiresPaid={!isProfessional} />
          <NavItem to="/hub" icon="💬" label="Dads Hub Chat" />
          <NavItem to="/messages" icon="✉️" label="Peer Connections" requiresPaid={!isProfessional} badgeCount={unreadDMs} totalCount={connectionCount} mentionsCount={unreadMentions} />
          <NavItem to="/broadcast" icon="📡" label="Live Broadcast" isBroadcastTab />
          <NavItem to="/resources" icon="📂" label="Resource Library" requiresPaid={!isProfessional} />
          <NavItem to="/settings" icon="⚙️" label="Settings" />
          {isProfessional && <div className="pt-4 pb-2 px-6 text-[11px] font-bold text-md-on-surface-variant uppercase tracking-widest">Management</div>}
          {isProfessional && <NavItem to="/manager" icon="👥" label="Group Members" />}
          {isAdmin && <NavItem to="/admin" icon="⚡" label="Admin Portal" />}
        </nav>

        {!isPaid && !isProfessional && (
          <div className="px-6 mb-4 mt-2">
              <div className="bg-md-primary-container rounded-3xl p-5 shadow-sm text-md-on-primary-container">
                  <div className="flex justify-between items-start mb-2">
                    <p className="font-bold text-sm">Parent PRO</p>
                    <span className="font-bold text-xs">£25</span>
                  </div>
                  <p className="text-xs mb-4 leading-relaxed opacity-80 font-medium">Unlock 20+ training modules, legal templates & access.</p>
                  <button onClick={() => navigate('/subscription')} className="w-full bg-md-primary text-md-on-primary text-xs font-bold rounded-full py-2.5 hover:shadow-md transition-all active:scale-95">Upgrade Account</button>
              </div>
          </div>
        )}

        {isProfessional && (
            <div className="px-6 mb-4 mt-2">
                <div className="bg-md-surface-container-high rounded-3xl p-5">
                    <p className="text-md-on-surface-variant font-bold text-[10px] uppercase tracking-widest mb-1.5">Network Status</p>
                    <p className="text-md-on-surface text-xs font-bold flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full bg-md-tertiary animate-pulse shadow-sm"></span>
                        {isDadsMatterTeam ? "Dads Matter Team" : (isAdmin ? "Administrator" : "Professional Access")}
                    </p>
                </div>
            </div>
        )}

        <div className="p-6 mt-auto border-t border-md-outline-variant/50">
            <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-md-secondary-container flex items-center justify-center text-sm font-bold text-md-on-secondary-container">{userInitials}</div>
                <div className="overflow-hidden">
                    <p className="text-sm font-bold truncate text-md-on-surface">{userProfile?.displayName}</p>
                    <p className="text-xs text-md-on-surface-variant capitalize font-medium">{userProfile?.role}</p>
                </div>
            </div>
            <button onClick={handleLogout} className="w-full py-2.5 text-xs font-bold border border-md-outline/50 rounded-full text-md-on-surface hover:bg-md-surface-variant transition-colors">Sign Out</button>
        </div>
      </aside>

      {/* Main Content Area */}
      <main className={`flex-1 flex flex-col h-full relative ${fluid ? 'overflow-hidden pb-[80px] md:pb-0' : 'overflow-y-auto pb-[88px] md:pb-0'}`}>
        <div className={`${fluid ? 'w-full h-full' : 'max-w-7xl mx-auto p-4 md:p-8 w-full'} flex flex-col flex-1`}>{children}</div>
      </main>

      {/* Mobile Bottom Navigation: M3 Bottom App Bar */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-md-surface-container text-md-on-surface border-t border-md-outline-variant/30 flex items-center justify-around px-2 pt-2 pb-4 z-[100] shadow-[0_-8px_16px_rgba(0,0,0,0.03)]">
        <MobileTab to="/home" icon="🏠" label="Home" />
        <MobileTab to="/dashboard" icon="🎓" label="Training" requiresPaid={!isProfessional} />
        <MobileTab to="/resources" icon="📂" label="Library" requiresPaid={!isProfessional} />
        <MobileTab to="/hub" icon="💬" label="Chat" />
        <MobileTab to="/messages" icon="✉️" label="Connect" requiresPaid={!isProfessional} badgeCount={unreadDMs} mentionsCount={unreadMentions} />
        <MobileTab to="/broadcast" icon="📡" label="Live" isBroadcastTab />
        <button 
          onClick={() => setIsMoreMenuOpen(true)}
          className={`flex flex-col items-center justify-center flex-1 py-1 gap-1 text-md-on-surface-variant relative transition-colors`}
        >
          <div className="relative px-4 py-1 rounded-full bg-transparent transition-colors">
            <span className="text-xl block">☰</span>
          </div>
          <span className="text-[10px] tracking-wide font-medium">More</span>
        </button>
      </nav>

      {/* Mobile "More" Menu Overlay */}
      {isMoreMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[110] animate-fade-in">
          <div className="absolute inset-0 bg-md-on-surface/40 backdrop-blur-sm" onClick={() => setIsMoreMenuOpen(false)} />
          <div className="absolute bottom-0 left-0 right-0 bg-md-surface-container rounded-t-3xl p-6 shadow-2xl animate-slide-up">
            <div className="w-12 h-1.5 bg-md-outline-variant rounded-full mx-auto mb-6" />
            <div className="space-y-4">
              <div className="flex items-center gap-4 border-b border-md-outline-variant/50 pb-5">
                <div className="w-14 h-14 rounded-full bg-md-secondary-container flex items-center justify-center text-md-on-secondary-container font-bold text-xl shadow-sm">{userInitials}</div>
                <div>
                  <p className="font-bold text-md-on-surface text-lg">{userProfile?.displayName}</p>
                  <p className="text-sm font-medium text-md-on-surface-variant capitalize">Account: {isDadsMatterTeam ? "Team Member" : userProfile?.role}</p>
                </div>
              </div>
              
              <div className="py-2 flex flex-col gap-1">
                  <button onClick={() => { navigate('/settings'); setIsMoreMenuOpen(false); }} className="w-full text-left p-3 rounded-2xl text-md-on-surface font-medium text-base flex items-center gap-4 hover:bg-md-surface-variant/50 transition-colors">
                    <span className="text-xl">⚙️</span> Settings & Privacy
                  </button>
                  <button onClick={() => { navigate('/sandbox'); setIsMoreMenuOpen(false); }} className="w-full text-left p-3 rounded-2xl text-md-on-surface font-medium text-base flex items-center gap-4 hover:bg-md-surface-variant/50 transition-colors">
                    <span className="text-xl">⚖️</span> LiP Sandbox <span className="text-[10px] ml-auto uppercase font-bold tracking-widest text-indigo-500 bg-indigo-50 px-2 py-0.5 rounded-full">Beta</span>
                  </button>
                  {isProfessional && (
                       <button onClick={() => { navigate('/manager'); setIsMoreMenuOpen(false); }} className="w-full text-left p-3 rounded-2xl text-md-on-surface font-medium text-base flex items-center gap-4 hover:bg-md-surface-variant/50 transition-colors">
                        <span className="text-xl">👥</span> Group Members
                      </button>
                  )}
                  {isAdmin && (
                       <button onClick={() => { navigate('/admin'); setIsMoreMenuOpen(false); }} className="w-full text-left p-3 rounded-2xl text-md-on-surface font-medium text-base flex items-center gap-4 hover:bg-md-surface-variant/50 transition-colors">
                        <span className="text-xl">⚡</span> Admin Portal
                      </button>
                  )}
              </div>
              
              <button 
                onClick={handleLogout}
                className="w-full bg-md-surface text-md-on-surface border border-md-outline font-bold py-3.5 rounded-full text-sm hover:bg-md-surface-variant transition-colors"
              >
                Sign Out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Layout;
